/**
 * Icon — enveloppe unique des icônes Lucide.
 *
 * @remarks
 * Trois tailles, branchées sur les tokens `--icon-size-*` : elles suivent le
 * réglage Font Size, comme le texte à côté duquel l'icône est posée. Avant
 * SAAS-1184, 628 icônes passaient une taille en pixels (18 valeurs
 * différentes) et ces tokens n'étaient lus nulle part.
 *
 * | taille | token                | à 15px de base | usage                          |
 * | ------ | -------------------- | -------------- | ------------------------------ |
 * | `sm`   | `--icon-size-xs`     | ~11,6px        | puces, badges, texte `text-xs` |
 * | `md`   | `--icon-size-sm`     | ~13,8px        | boutons, lignes de liste       |
 * | `lg`   | `--icon-size-md`     | ~18,5px        | titres, actions principales    |
 *
 * Au-delà (états vides, illustrations), l'icône n'est plus un glyphe d'interface :
 * elle garde sa taille propre via `className`.
 *
 * **A11y** : décoratif par défaut (`aria-hidden`). Passer `label` quand l'icône
 * est seule à porter le sens : elle devient `role="img"`.
 */

import type { LucideIcon, LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export type IconSize = 'sm' | 'md' | 'lg';

export interface IconProps extends Omit<LucideProps, 'size' | 'ref' | 'children'> {
  /** Le glyphe Lucide à afficher. Pour un concept courant, voir `ICONS`. */
  icon: LucideIcon;
  size?: IconSize;
  /** Libellé lecteur d'écran. Fourni → `role="img"` ; absent → décoratif. */
  label?: string;
}

const SIZES: Record<IconSize, string> = {
  sm: 'size-(--icon-size-xs)',
  md: 'size-(--icon-size-sm)',
  lg: 'size-(--icon-size-md)',
};

export function Icon({ icon: Glyph, size = 'md', label, className, ...rest }: IconProps) {
  const a11y =
    label !== undefined ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const };

  return <Glyph {...a11y} className={cn('shrink-0', SIZES[size], className)} {...rest} />;
}
